import { colors } from '@dixa/lib-shared-tokens';
import { Timer } from 'lib-shared-legacy-components';
import { useEffect, useMemo } from 'react';
import { useOutletContext } from 'react-router-dom';
import { useMount, useUnmount } from 'react-use';
import { DeepWritable } from 'ts-essentials';

import { statisticsApi } from 'apis/statistics';
import { websocketActions } from 'app/middleware/websocket/actions/websocket';
import { WebSocketStatisticsDashboardQueueChangedInboundMessage } from 'middleware/webSocket';
import { pubSub } from 'utils/pubSub';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppPathParams } from 'utils/url/useAppPathParams';

import {
  TvDashboardMetric,
  type TvDashboardMetricItem,
} from '../../components/metric';
import { TvDashboardViewOutletContext } from '../../tvDashboard.view';

import styles from './tvLegacyDashboard.view.module.scss';

export const TvLegacyDashboardView = (): JSX.Element => {
  const dispatch = useAppDispatch();
  const { queueId } = useAppPathParams('/tv/dashboard/:queueId');

  const { queue, isLoading } = statisticsApi.useReadDashboardQueuesByIdQuery(
    { ids: [queueId] },
    {
      // Automatically refetch the dashboard data via an HTTP request every hour to ensure the
      // access/refresh tokens are refreshed and the TV dashboard remains logged in indefinitely.
      pollingInterval: 1000 * 60 * 60,
      selectFromResult: ({ data, ...rest }) => ({
        ...rest,
        queue: data?.data.dashboard_entries[0],
      }),
    },
  );

  const { setTitle } = useOutletContext<TvDashboardViewOutletContext>();

  useEffect(() => {
    if (queue) {
      setTitle(queue.queue.name);
    }
  }, [queue, setTitle]);

  useMount(() => {
    dispatch(
      websocketActions.request({
        type: 'connection:subscribe',
        params: {
          type: 'queue_dashboard',
        },
      }),
    );
  });

  useUnmount(() => {
    dispatch(
      websocketActions.request({
        type: 'connection:unsubscribe',
        params: {
          type: 'queue_dashboard',
        },
      }),
    );
  });

  useEffect(() => {
    const unsubscribe = pubSub.subscribe(
      'statisticsDashboardQueueChanged',
      handleDashboardQueueChanged,
    );

    return () => unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDashboardQueueChanged = (
    payload: WebSocketStatisticsDashboardQueueChangedInboundMessage,
  ) => {
    const updatedQueue = payload.params.dashboard.dashboard_entries.find(
      (queue) => queue.queue_id === queueId,
    );

    if (updatedQueue) {
      dispatch(
        statisticsApi.util.updateQueryData(
          'readDashboardQueuesById',
          {
            ids: [queueId],
          },
          (draft) => {
            const mutableData = draft.data as DeepWritable<typeof draft.data>;
            mutableData.dashboard_entries = [updatedQueue];
          },
        ),
      );
    }
  };

  const metrics = useMemo((): readonly TvDashboardMetricItem[] => {
    const inQueue = queue?.conversations_in_queue ?? 0;
    const total = queue?.total_conversations ?? 0;
    const answered = queue?.answered_conversations ?? 0;
    const transferred = queue?.transfered_conversations ?? 0;
    const abandoned = queue?.abandoned_conversations ?? 0;
    const slaPercentage = queue?.service_level ?? 0;
    const answeredPercentage = (answered / (abandoned + answered)) * 100;
    const phoneAgents = queue?.available_agents.pstn_phone ?? 0;
    const chatAgents = queue?.available_agents.widget_chat ?? 0;
    const emailAgents = queue?.available_agents.email ?? 0;
    const messagingAgents = queue?.available_agents.facebook_messenger ?? 0;

    return [
      {
        title: 'In queue',
        stat: inQueue,
        color: colors.orange[400],
      },
      {
        title: 'Total',
        stat: total,
      },
      {
        title: 'Answered',
        stat: answered,
        color: colors.green[500],
      },
      {
        title: 'Timeouts',
        stat: transferred,
      },
      {
        title: 'Abandoned',
        stat: abandoned,
        color: colors.red[500],
      },
      {
        title: 'SLA',
        stat: slaPercentage ? `${slaPercentage.toFixed(1)}%` : '-',
      },
      {
        title: 'Answered',
        stat: answeredPercentage ? `${answeredPercentage.toFixed(1)}%` : '-',
        color: colors.green[500],
      },
      {
        title: 'Current longest wait time',
        stat: queue?.longest_wait ? (
          <Timer
            asCounter={false}
            start
            startAt={queue.longest_wait}
            textFitProps={{ max: 70 }}
          />
        ) : (
          '00:00:00'
        ),
      },
      {
        title: 'Average handling time',
        stat: queue?.average_handling_time ? (
          <Timer
            asCounter
            start={false}
            startAt={queue.average_handling_time}
            textFitProps={{ max: 70 }}
          />
        ) : (
          '00:00:00'
        ),
      },
      {
        title: 'Average wait time',
        stat: queue?.average_waiting_time ? (
          <Timer
            asCounter
            start={false}
            startAt={queue.average_waiting_time}
            textFitProps={{ max: 70 }}
          />
        ) : (
          '00:00:00'
        ),
      },
      ...([
        phoneAgents > 0 && { title: 'Phone agents', stat: phoneAgents },
        chatAgents > 0 && { title: 'Chat agents', stat: chatAgents },
        emailAgents > 0 && { title: 'Email agents', stat: emailAgents },
        messagingAgents > 0
          ? { title: 'Messaging agents', stat: messagingAgents }
          : null,
      ].filter(Boolean) as typeof metrics),
    ];
  }, [queue]);

  return (
    <div className={styles.grid}>
      {metrics.map((metric, index) => (
        <TvDashboardMetric
          key={index}
          className={styles.item}
          metric={metric}
          isLoading={isLoading}
        />
      ))}
    </div>
  );
};
